import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ── Apple logo ── */
const AppleLogo = () => (
  <svg width="18" height="22" viewBox="0 0 814 1000" fill="white">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.3-168.6-107.4C67.3 772.7 1 651.4 1 536.2c0-188.1 123.3-287.7 244.6-287.7 62.7 0 114.9 41.4 154.5 41.4 37.9 0 97.9-43.9 170.2-43.9 27.2 0 109.2 2.6 166 98.8zm-265.4-194.4c31.3-37.3 53.5-89.3 53.5-141.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28 31.3-55.1 83.2-55.1 135.9 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.1-69.7z" />
  </svg>
);

/* ── Google Play colorful logo ── */
const GooglePlayLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <defs>
      <linearGradient id="fg1" x1="7.071" y1="14.99" x2="29.4" y2="-7.34" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#00A0FF"/><stop offset="1" stopColor="#00E3FF"/>
      </linearGradient>
      <linearGradient id="fg2" x1="33.835" y1="24" x2="5.13" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#FFE000"/><stop offset="1" stopColor="#FF9C00"/>
      </linearGradient>
      <linearGradient id="fg3" x1="21.863" y1="26.54" x2="3.583" y2="44.82" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#FF3A44"/><stop offset="1" stopColor="#C31162"/>
      </linearGradient>
      <linearGradient id="fg4" x1="4.475" y1="2.87" x2="17.796" y2="16.19" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#32A071"/><stop offset="1" stopColor="#00F076"/>
      </linearGradient>
    </defs>
    <path fill="url(#fg1)" d="M7.076 7.41C6.725 7.79 6.5 8.37 6.5 9.13v29.74c0 .76.225 1.34.576 1.72l.09.088 16.65-16.65v-.392L7.166 7.322l-.09.088z"/>
    <path fill="url(#fg2)" d="m29.052 24.39-5.54-5.542v-.393l5.54-5.542.125.07 6.562 3.73c1.875 1.065 1.875 2.807 0 3.872l-6.562 3.73-.125.075z"/>
    <path fill="url(#fg3)" d="m29.177 24.315-5.665-5.665-16.436 16.436c.618.654 1.638.735 2.79.082l19.31-10.853"/>
    <path fill="url(#fg4)" d="M29.177 23.685 9.866 12.832c-1.152-.653-2.172-.572-2.79.082L23.512 29.35l5.665-5.665z"/>
  </svg>
);

/* ── Phone icon for fab ── */
const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
    <line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
);

const FloatingDownload = () => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: "fixed", bottom: 32, right: 32, zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 }}>

      {/* ── Expanded panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.85, y: 20, transformOrigin: "bottom right" }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 20 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            style={{
              width: 280,
              borderRadius: 20,
              background: "rgba(12,12,12,0.92)",
              border: "1px solid rgba(255,28,53,0.25)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              boxShadow: "0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05), 0 0 40px rgba(255,28,53,0.12)",
              overflow: "hidden",
            }}
          >
            {/* Top accent bar */}
            <div style={{ height: 3, background: "linear-gradient(90deg,#FF1C35,#ff6b6b,#FF1C35)", backgroundSize: "200% 100%", animation: "shimmer 2s linear infinite" }} />

            {/* Header */}
            <div style={{ padding: "18px 20px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#FF1C35", fontWeight: 900, fontSize: 14, letterSpacing: 2 }}>MIRE</span>
                  <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, letterSpacing: 3 }}>DRAMA</span>
                </div>
                {/* Star rating */}
                <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: i < 4 ? "#FF1C35" : "rgba(255,28,53,0.3)", fontSize: 10 }}>★</span>
                  ))}
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginLeft: 3 }}>4.8</span>
                </div>
              </div>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", margin: "0 0 16px", lineHeight: 1.6 }}>
                东南亚最大本地短剧平台 · 500K+ 用户
              </p>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "0 20px" }} />

            {/* Download buttons */}
            <div style={{ padding: "14px 16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>

              {/* App Store */}
              <motion.a
                href="https://apps.apple.com/cn/app/mire-drama/id6751633685"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03, borderColor: "rgba(255,28,53,0.5)" } as never}
                whileTap={{ scale: 0.97 } as never}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 16px", borderRadius: 12, cursor: "pointer",
                  background: "#000",
                  border: "1px solid rgba(255,255,255,0.12)",
                  textDecoration: "none",
                  transition: "border-color 0.2s",
                }}
              >
                <AppleLogo />
                <div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", letterSpacing: 1, textTransform: "uppercase" }}>Download on the</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>App Store</div>
                </div>
                {/* Arrow */}
                <svg style={{ marginLeft: "auto", opacity: 0.3 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </motion.a>

              {/* Google Play */}
              <motion.a
                href="https://play.google.com/store/apps/details?id=com.miredrama.app&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03, borderColor: "rgba(255,28,53,0.5)" } as never}
                whileTap={{ scale: 0.97 } as never}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 16px", borderRadius: 12, cursor: "pointer",
                  background: "#000",
                  border: "1px solid rgba(255,255,255,0.12)",
                  textDecoration: "none",
                  transition: "border-color 0.2s",
                }}
              >
                <GooglePlayLogo />
                <div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", letterSpacing: 1, textTransform: "uppercase" }}>Get it on</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>Google Play</div>
                </div>
                <svg style={{ marginLeft: "auto", opacity: 0.3 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </motion.a>

              {/* Bottom tip */}
              <div style={{ textAlign: "center", fontSize: 10, color: "rgba(255,255,255,0.2)", paddingTop: 2 }}>
                Available on iOS &amp; Android · 支持 iOS 与 Android
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FAB button ── */}
      <motion.button
        onClick={() => setOpen(v => !v)}
        whileHover={{ scale: 1.08 } as never}
        whileTap={{ scale: 0.93 } as never}
        animate={open ? { rotate: 0 } : { rotate: 0 }}
        style={{
          width: 56, height: 56, borderRadius: "50%",
          background: "linear-gradient(135deg,#FF1C35,#c0001a)",
          border: "none", cursor: "pointer",
          boxShadow: "0 8px 32px rgba(255,28,53,0.55), 0 0 0 1px rgba(255,255,255,0.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", overflow: "visible",
        }}
      >
        {/* Pulse ring */}
        {!open && (
          <motion.span
            animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeOut" }}
            style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              border: "2px solid rgba(255,28,53,0.6)",
              pointerEvents: "none",
            }}
          />
        )}

        {/* Icon: phone → X */}
        <AnimatePresence mode="wait">
          {open ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth="2.5" strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12"/>
            </motion.svg>
          ) : (
            <motion.div
              key="phone"
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.7, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <PhoneIcon />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      <style>{`
        @keyframes shimmer { 0%{background-position:0 0} 100%{background-position:200% 0} }
        @media(max-width:480px){
          .float-panel{width:calc(100vw - 40px)!important;right:20px!important;}
        }
      `}</style>
    </div>
  );
};

export default FloatingDownload;
